import React, { useState, useRef, useLayoutEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { toPng } from 'html-to-image'
import useMagnetic from '../../hooks/useMagnetic'

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const FATAL_FLAWS = ['vanity', 'envy', 'pride', 'wrath', 'betrayal', 'ambition', 'greed', 'despair']
const POWER_SOURCES = [
  'cursed object',
  'ancestral magic',
  'scientific accident',
  'dark deal',
  'mutation',
  'eldritch awakening',
  'technological augmentation',
]

const ERROR_COPY = {
  rate_limit: 'You have forged 5 villains in the last 24h. Cool off and return.',
  not_subscribed: 'Membership required to forge a villain.',
  no_member: 'No member record found for your account. Contact support.',
  invalid_token: 'Session expired. Sign in again.',
  no_auth: 'Sign in to forge a villain.',
  missing_anthropic_key: 'The forge is cooling — backend key missing. Try again later.',
  llm_parse_failed: 'The forge sputtered. Try again — different inputs help.',
  llm_error: 'The forge could not reach the void. Try again in a moment.',
  db_insert: 'Forged, but the archive refused to record it. Try again.',
  bad_json: 'Bad request.',
  missing_fields: 'Fill in every field before forging.',
}

export default function VillainOrigin({ session }) {
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [childhood, setChildhood] = useState('')
  const [fatalFlaw, setFatalFlaw] = useState('')
  const [powerSource, setPowerSource] = useState('')
  const [status, setStatus] = useState('idle') // idle | forging | done | error
  const [villain, setVillain] = useState(null)
  const [errorMsg, setErrorMsg] = useState('')
  const [downloading, setDownloading] = useState(false)
  const cardRef = useRef(null)
  const stampRef = useRef(null)
  const originRef = useRef(null)
  const weaknessRef = useRef(null)
  const forgeBtnRef = useMagnetic()

  const villainLetters = useMemo(
    () => (villain ? Array.from(villain.villain_name) : []),
    [villain]
  )

  // Split origin into sentence-chunks so each fades in independently. Falls back
  // to the whole string if the regex finds no boundaries.
  const originSentences = useMemo(() => {
    if (!villain) return []
    const parts = villain.origin.split(/(?<=[.!?])\s+/).filter(s => s.trim().length > 0)
    return parts.length ? parts : [villain.origin]
  }, [villain])

  // 7-stage dossier reveal is now driven by CSS @keyframes (dossierCardIn,
  // dossierStampIn, dossierRedactSweep, dossierLetterIn, dossierLineIn,
  // dossierPowerIn, dossierWeaknessFade) with per-element animation-delay
  // inlined on the JSX below. Each element's animation kicks in when it
  // mounts; mounting only happens when status === 'done', so the choreography
  // plays exactly once per forge. CSS-driven by the browser compositor — can't
  // get stranded the way anime.set + anime() did on Waves 3-5.
  // Effect kept as diagnostic-only so the live console reports the element
  // counts we'd expect to see animating.
  useLayoutEffect(() => {
    if (status !== 'done' || !villain || !cardRef.current) return undefined
    const card = cardRef.current
    const redacts = card.querySelectorAll('.dossier-redact')
    const letters = card.querySelectorAll('.dossier-letter')
    const originLines = card.querySelectorAll('.dossier-origin-line')
    const powerLis = card.querySelectorAll('.dossier-power-li')
    // eslint-disable-next-line no-console
    console.log('[dossier] mounted — redact:', redacts.length,
      'letters:', letters.length, 'origin-lines:', originLines.length,
      'powers:', powerLis.length, '· prefers-reduced-motion:', prefersReducedMotion())
    return undefined
  }, [status, villain])

  const submit = async () => {
    if (!name.trim() || !childhood.trim() || !fatalFlaw || !powerSource) {
      setErrorMsg('Fill in every field before forging.')
      setStatus('error')
      return
    }
    setStatus('forging')
    setErrorMsg('')
    try {
      const res = await fetch('/.netlify/functions/villain-generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token || ''}`,
        },
        body: JSON.stringify({
          name: name.trim(),
          childhood: childhood.trim(),
          fatal_flaw: fatalFlaw,
          power_source: powerSource,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setErrorMsg(ERROR_COPY[data.error] || data.message || 'Forge failed. Try again.')
        setStatus('error')
        return
      }
      setVillain(data)
      setStatus('done')
    } catch (e) {
      setErrorMsg('Network error. Check your connection and try again.')
      setStatus('error')
    }
  }

  const reset = () => {
    setVillain(null)
    setName('')
    setChildhood('')
    setFatalFlaw('')
    setPowerSource('')
    setStatus('idle')
    setErrorMsg('')
  }

  const downloadImage = async () => {
    if (!cardRef.current || !villain) return
    setDownloading(true)
    try {
      if (document.fonts && document.fonts.ready) await document.fonts.ready
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: '#080004',
      })
      const slug = villain.villain_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'villain'
      const link = document.createElement('a')
      link.download = `villain-${slug}.png`
      link.href = dataUrl
      link.click()
    } catch (e) {
      console.error('download failed:', e)
      setErrorMsg('Download failed. Try a screenshot instead.')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div style={{ background: '#000', color: '#ff0055', fontFamily: "'Share Tech Mono', monospace", minHeight: '100vh', overflowX: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=Bebas+Neue&family=Space+Grotesk:wght@700;800&family=Cinzel+Decorative:wght@700;900&family=Pirata+One&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        .scanlines{position:fixed;inset:0;background:repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,0.08) 2px,rgba(0,0,0,0.08) 3px);pointer-events:none;z-index:100}
        .grid-bg{position:fixed;inset:0;background-image:linear-gradient(rgba(255,0,80,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,0,80,0.025) 1px,transparent 1px);background-size:28px 28px;pointer-events:none;z-index:0}
        @keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.3;transform:scale(1.4)}}
        @keyframes shimmer{0%{left:-100%}100%{left:200%}}
        @keyframes flicker{0%,18%,22%,25%,53%,57%,100%{opacity:1}19%,21%,23%,55%{opacity:0.6}}
        @keyframes redactionSweep{0%{transform:scaleX(0);transform-origin:left}45%{transform:scaleX(1);transform-origin:left}55%{transform:scaleX(1);transform-origin:right}100%{transform:scaleX(0);transform-origin:right}}
        @keyframes dossierIn{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
        @keyframes forging{0%{opacity:0.5}50%{opacity:1}100%{opacity:0.5}}
        .v-input,.v-textarea{width:100%;background:rgba(255,0,80,0.03);border:1px solid rgba(255,0,80,0.2);color:#ff0055;font-family:'Share Tech Mono',monospace;font-size:12px;padding:12px;outline:none;transition:border-color 0.2s}
        .v-input:focus,.v-textarea:focus{border-color:#ff0055}
        .v-input::placeholder,.v-textarea::placeholder{color:rgba(255,0,80,0.3)}
        .v-textarea{resize:none;line-height:1.6}
        .v-chip{background:transparent;border:1px solid rgba(255,0,80,0.3);color:rgba(255,0,80,0.75);font-family:'Share Tech Mono',monospace;font-size:10px;padding:8px 12px;letter-spacing:1px;cursor:pointer;text-transform:uppercase;transition:all 0.15s}
        .v-chip:hover{border-color:#ff0055;color:#ff0055}
        .v-chip.active{background:#ff0055;color:#000;border-color:#ff0055;box-shadow:0 0 12px rgba(255,0,80,0.4)}
        .forge-btn{width:100%;background:#ff0055;color:#000;border:none;padding:18px;font-family:'Share Tech Mono',monospace;font-size:14px;letter-spacing:2px;cursor:pointer;font-weight:700;position:relative;overflow:hidden;transition:all 0.2s}
        .forge-btn::before{content:'';position:absolute;top:0;left:-100%;width:100%;height:100%;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.18),transparent);animation:shimmer 3s ease infinite}
        .forge-btn:hover{background:#fff;box-shadow:0 0 40px rgba(255,0,80,0.5)}
        .forge-btn:disabled{opacity:0.5;cursor:not-allowed}
        .ghost-btn{display:inline-block;background:none;border:1px solid rgba(255,0,80,0.4);color:#ff0055;font-family:'Share Tech Mono',monospace;font-size:11px;padding:11px 16px;cursor:pointer;letter-spacing:1.5px;text-decoration:none;transition:all 0.2s}
        .ghost-btn:hover{background:rgba(255,0,80,0.08);border-color:#ff0055}
        .dossier-card{aspect-ratio:9/16;width:100%;max-width:360px;margin:0 auto;background:radial-gradient(ellipse at 30% 20%,#15000a 0%,#080004 60%,#040002 100%);border:1px solid rgba(255,0,80,0.5);position:relative;overflow:hidden;padding:22px 20px 80px;box-shadow:0 0 40px rgba(255,0,80,0.25),inset 0 0 40px rgba(255,0,80,0.06);animation:dossierCardIn 400ms cubic-bezier(0.16,1,0.3,1) backwards}
        .dossier-noise{position:absolute;inset:0;opacity:0.06;mix-blend-mode:overlay;pointer-events:none;background-image:radial-gradient(rgba(255,255,255,0.4) 1px,transparent 1px),radial-gradient(rgba(255,0,80,0.6) 1px,transparent 1px);background-size:3px 3px,7px 7px;background-position:0 0,1px 1px}
        .dossier-stamp{position:absolute;top:24px;right:-22px;transform:rotate(18deg);border:2px solid #ff0055;color:#ff0055;padding:4px 18px;font-family:'Space Grotesk',sans-serif;font-weight:800;font-size:12px;letter-spacing:3px;opacity:0.75;background:rgba(0,0,0,0.4);animation:dossierStampIn 500ms cubic-bezier(0.34,1.56,0.64,1) 300ms backwards}
        .dossier-redact{position:absolute;height:14px;background:#ff0055;animation:dossierRedactSweep 600ms cubic-bezier(0.45,0,0.55,1) backwards}
        .dossier-name{font-family:'Cinzel Decorative',serif;font-weight:900;font-size:34px;line-height:1;letter-spacing:-1px;color:#ff0055;text-shadow:0 0 18px rgba(255,0,80,0.55),0 0 4px rgba(0,0,0,0.8);word-break:break-word;animation:flicker 6s linear infinite}
        .dossier-letter{display:inline-block;white-space:pre;animation:dossierLetterIn 60ms ease-out backwards}
        .dossier-origin-line{animation:dossierLineIn 200ms linear backwards}
        .dossier-power-li{animation:dossierPowerIn 300ms ease-out backwards}
        .dossier-weakness{animation:dossierWeaknessFade 700ms cubic-bezier(0.45,0,0.55,1) 1800ms backwards}
        @keyframes dossierCardIn{from{opacity:0;transform:scale(0.95)}to{opacity:1;transform:scale(1)}}
        @keyframes dossierStampIn{from{opacity:0;transform:scale(1.5) rotate(-25deg)}to{opacity:0.75;transform:scale(1) rotate(18deg)}}
        @keyframes dossierRedactSweep{0%{opacity:1;transform:translateX(-110%)}67%{opacity:1}100%{opacity:0;transform:translateX(110%)}}
        @keyframes dossierLetterIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}
        @keyframes dossierLineIn{from{opacity:0}to{opacity:1}}
        @keyframes dossierPowerIn{from{opacity:0;transform:translateX(-8px)}to{opacity:1;transform:translateX(0)}}
        @keyframes dossierWeaknessFade{0%{opacity:0}50%{opacity:0.6}100%{opacity:1}}
        @media (prefers-reduced-motion: reduce){.dossier-card,.dossier-stamp,.dossier-redact,.dossier-letter,.dossier-origin-line,.dossier-power-li,.dossier-weakness{animation:none !important}}
        .dossier-attr{position:absolute;bottom:14px;right:16px;text-align:right;font-size:9px;letter-spacing:1px;color:rgba(255,0,80,0.7)}
        .dossier-attr-num{font-family:'Bebas Neue',sans-serif;font-size:22px;color:#ff0055;line-height:1;letter-spacing:-1px}
        .dossier-corner{position:absolute;width:14px;height:14px;border:1px solid rgba(255,0,80,0.6)}
        .forging{font-family:'Bebas Neue',sans-serif;font-size:48px;color:#ff0055;letter-spacing:4px;text-shadow:0 0 24px rgba(255,0,80,0.5);animation:forging 1.4s ease infinite}
      `}</style>

      <div className="scanlines" />
      <div className="grid-bg" />

      <div style={{ maxWidth: 520, margin: '0 auto', padding: '0 0 80px', position: 'relative', zIndex: 2 }}>

        {/* TOP BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid rgba(255,0,80,0.15)' }}>
          <button className="ghost-btn" style={{ padding: '6px 10px', fontSize: 10 }} onClick={() => navigate('/dashboard')}>← DASHBOARD</button>
          <div style={{ fontSize: 10, color: 'rgba(255,0,80,0.65)', letterSpacing: 2 }}>// 04 · VILLAIN_ORIGIN</div>
        </div>

        {/* HERO */}
        <div style={{ padding: '24px 20px 8px' }}>
          <div style={{ fontSize: 9, letterSpacing: 3, color: 'rgba(255,0,80,0.6)', marginBottom: 8 }}>// FORGE_PROTOCOL</div>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 56, color: '#ff0055', letterSpacing: -2, lineHeight: 0.9, textShadow: '0 0 30px rgba(255,0,80,0.3)' }}>
            FORGE YOUR<br/>VILLAIN
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,0,80,0.65)', marginTop: 10, lineHeight: 1.6, paddingLeft: 12, borderLeft: '2px solid rgba(255,0,80,0.4)' }}>
            Four answers. Two seconds. One villain. Screenshot the dossier. Post it. Watch the algorithm bow.
          </div>
        </div>

        {/* FORM or LOADING or OUTPUT */}
        {status !== 'done' && (
          <div style={{ padding: '20px' }}>
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 9, letterSpacing: 2, color: 'rgba(255,0,80,0.65)', marginBottom: 6 }}>// NAME_OR_ALIAS</div>
              <input
                className="v-input"
                placeholder="who are you, really"
                maxLength={80}
                value={name}
                onChange={e => setName(e.target.value)}
                disabled={status === 'forging'}
              />
            </div>

            <div style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, letterSpacing: 2, color: 'rgba(255,0,80,0.65)', marginBottom: 6 }}>
                <span>// CHILDHOOD_DETAIL — what broke you</span>
                <span>{childhood.length}/200</span>
              </div>
              <textarea
                className="v-textarea"
                rows={3}
                placeholder="the one memory you don't say out loud"
                maxLength={200}
                value={childhood}
                onChange={e => setChildhood(e.target.value)}
                disabled={status === 'forging'}
              />
            </div>

            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 9, letterSpacing: 2, color: 'rgba(255,0,80,0.65)', marginBottom: 8 }}>// FATAL_FLAW</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {FATAL_FLAWS.map(f => (
                  <button
                    key={f}
                    className={`v-chip ${fatalFlaw === f ? 'active' : ''}`}
                    onClick={() => setFatalFlaw(f)}
                    disabled={status === 'forging'}
                    type="button"
                  >{f}</button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 9, letterSpacing: 2, color: 'rgba(255,0,80,0.65)', marginBottom: 8 }}>// POWER_SOURCE</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {POWER_SOURCES.map(p => (
                  <button
                    key={p}
                    className={`v-chip ${powerSource === p ? 'active' : ''}`}
                    onClick={() => setPowerSource(p)}
                    disabled={status === 'forging'}
                    type="button"
                  >{p}</button>
                ))}
              </div>
            </div>

            {status === 'forging' ? (
              <div style={{ textAlign: 'center', padding: '30px 0' }}>
                <div className="forging">▶ FORGING...</div>
                <div style={{ fontSize: 10, color: 'rgba(255,0,80,0.55)', letterSpacing: 2, marginTop: 14 }}>
                  CONSULTING THE VOID · TYPESETTING THE DOSSIER · STAMPING CLASSIFIED
                </div>
              </div>
            ) : (
              <button ref={forgeBtnRef} className="forge-btn" onClick={submit} disabled={status === 'forging'}>
                ▶ FORGE_VILLAIN
              </button>
            )}

            {status === 'error' && errorMsg && (
              <div style={{ marginTop: 14, padding: '12px 14px', border: '1px solid rgba(255,0,80,0.4)', background: 'rgba(255,0,80,0.06)', fontSize: 11, color: '#ff0055', letterSpacing: 0.5, lineHeight: 1.5 }}>
                ▶ {errorMsg}
              </div>
            )}
          </div>
        )}

        {/* DOSSIER OUTPUT */}
        {status === 'done' && villain && (
          <div style={{ padding: '12px 20px 0' }}>
            <div ref={cardRef} className="dossier-card">
              {/* corners */}
              <div className="dossier-corner" style={{ top: 8, left: 8, borderRight: 'none', borderBottom: 'none' }} />
              <div className="dossier-corner" style={{ top: 8, right: 8, borderLeft: 'none', borderBottom: 'none' }} />
              <div className="dossier-corner" style={{ bottom: 8, left: 8, borderRight: 'none', borderTop: 'none' }} />
              <div className="dossier-corner" style={{ bottom: 8, right: 8, borderLeft: 'none', borderTop: 'none' }} />

              <div className="dossier-noise" />
              <div ref={stampRef} className="dossier-stamp">CLASSIFIED</div>

              {/* redaction sweeps over villain name area — stage 3 (~600ms) */}
              <div className="dossier-redact" style={{ top: 80, left: 0, right: 0, animationDelay: '600ms' }} />
              <div className="dossier-redact" style={{ top: 104, left: 0, right: 0, animationDelay: '680ms' }} />
              <div className="dossier-redact" style={{ top: 128, left: 0, right: 0, animationDelay: '760ms' }} />

              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ fontSize: 8, letterSpacing: 3, color: 'rgba(255,0,80,0.6)', marginBottom: 4 }}>// DOSSIER_NO_{String(villain.member_number).padStart(4, '0')}</div>
                <div style={{ fontSize: 9, letterSpacing: 2, color: 'rgba(255,0,80,0.85)', marginBottom: 18 }}>SUBJECT — DESIGNATION</div>

                <div className="dossier-name" aria-label={villain.villain_name}>
                  {villainLetters.map((ch, i) => (
                    <span key={i} className="dossier-letter" aria-hidden="true" style={{ animationDelay: `${900 + i * 40}ms` }}>
                      {ch === ' ' ? ' ' : ch}
                    </span>
                  ))}
                </div>

                <div style={{ height: 1, background: 'linear-gradient(90deg, #ff0055, transparent)', margin: '14px 0 12px' }} />

                <div style={{ fontSize: 8, letterSpacing: 2, color: 'rgba(255,0,80,0.7)', marginBottom: 6 }}>// ORIGIN</div>
                <div ref={originRef} style={{ fontSize: 11, color: 'rgba(255,0,80,0.92)', lineHeight: 1.55, marginBottom: 14 }}>
                  {originSentences.map((sent, i) => (
                    <span
                      key={i}
                      className="dossier-origin-line"
                      style={{ display: 'block', animationDelay: `${1300 + i * 150}ms` }}
                    >
                      {sent}
                    </span>
                  ))}
                </div>

                <div style={{ fontSize: 8, letterSpacing: 2, color: 'rgba(255,0,80,0.7)', marginBottom: 6 }}>// POWERS</div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px', fontSize: 11, color: 'rgba(255,0,80,0.92)', lineHeight: 1.5 }}>
                  {villain.powers.map((p, i) => (
                    <li
                      key={i}
                      className="dossier-power-li"
                      style={{ paddingLeft: 14, position: 'relative', marginBottom: 4, animationDelay: `${1500 + i * 100}ms` }}
                    >
                      <span style={{ position: 'absolute', left: 0, color: '#ff0055' }}>▸</span>{p}
                    </li>
                  ))}
                </ul>

                <div style={{ fontSize: 8, letterSpacing: 2, color: 'rgba(255,0,80,0.7)', marginBottom: 6 }}>// WEAKNESS</div>
                <div ref={weaknessRef} className="dossier-weakness" style={{ fontSize: 11, color: 'rgba(255,0,80,0.65)', fontStyle: 'italic', lineHeight: 1.5 }}>
                  {villain.weakness}
                </div>
              </div>

              {/* attribution */}
              <div className="dossier-attr">
                <div className="dossier-attr-num">#{villain.member_number}</div>
                <div>{villain.member_display_name}</div>
                <div style={{ fontSize: 7, opacity: 0.6, marginTop: 1 }}>2DOLLARSITES · FORGED</div>
              </div>
            </div>

            {/* ACTIONS */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
              <button className="forge-btn" onClick={downloadImage} disabled={downloading}>
                {downloading ? 'EXPORTING...' : '▶ DOWNLOAD AS IMAGE'}
              </button>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="ghost-btn" style={{ flex: 1 }} onClick={() => navigate('/app/rogues')}>▶ SEE ALL ROGUES</button>
                <button className="ghost-btn" style={{ flex: 1 }} onClick={reset}>▶ FORGE ANOTHER</button>
              </div>
            </div>

            {errorMsg && (
              <div style={{ marginTop: 14, padding: '12px 14px', border: '1px solid rgba(255,0,80,0.4)', background: 'rgba(255,0,80,0.06)', fontSize: 11, color: '#ff0055' }}>
                ▶ {errorMsg}
              </div>
            )}
          </div>
        )}

        <div style={{ padding: '24px 20px 12px', borderTop: '1px solid rgba(255,0,80,0.08)', marginTop: 24, display: 'flex', justifyContent: 'space-between', fontSize: 8, color: 'rgba(255,0,80,0.35)', letterSpacing: 1.5 }}>
          <span>2DOLLARSITES.COM</span>
          <span>FORGE OPEN 24/7</span>
          <span>EST.2026</span>
        </div>
      </div>
    </div>
  )
}
